import type { ColDef, ColTypeDef, GetRowIdParams } from 'ag-grid-community';
import { MasterDetailModule } from 'ag-grid-enterprise';
import { ServerSideBlotterSearchRequest, tradesBlotterApiConnector } from 'apiConnector/blotters';
import { PageLessFooter } from 'components/Blotters/pageless-footer';
import { useTradeBlotterServerSideGridOptions } from 'components/Blotters/Trades/Grid/hooks/useTradeBlotterServerSideGridOptions';
import { useTradesBlotterState } from 'components/Blotters/Trades/Grid/hooks/useTradesBlotterState';
import { SensitivityDetailCellRenderer } from 'components/Blotters/Trades/Grid/sensitivity-detail-cell-renderer';
import { ExternalFilter } from 'components/Blotters/Trades/Grid/utils';
import { CreditCheckBadge } from 'components/shared/Blotter/CreditCheckBadge/CreditCheckBadge.connect';
import { ServerSideBlotterGrid } from 'components/shared/Blotter/Grid/blotter-serverside';
import { useGetFilterValuesWithApi } from 'components/shared/Blotter/Grid/Hooks/useTranslateFilterInColumnDefinitions';
import { canSetActive, getHeaderForBlotterIntlKey } from 'components/shared/Blotter/utils';
import { TradesOrProductsBlotterRequest } from 'epic/blotters/tradesBlotter/tradesBlottersEpic';
import { useAppSelector } from 'hooks/useAppSelector';
import type { IBlotterTradeItem } from 'models/IBlotterItem';
import { useState } from 'react';
import { getIsUserInternal } from 'state/selectors';
import { httpClient } from 'utils/http/httpClient';

const modules = [MasterDetailModule];

export type TradesBlotterServerSideGridProps = {};

function getRowId({ data }: GetRowIdParams) {
  return data['tradeId'];
}

const tradesBlotterApi = tradesBlotterApiConnector(httpClient);
const searchItems = (req: ServerSideBlotterSearchRequest) => tradesBlotterApi.serverSideSearchTrades(req);
const getHeaderIntlKey = getHeaderForBlotterIntlKey('tradesBlotter');
type Props = { setLastBlotterRequest: (value: ServerSideBlotterSearchRequest) => void };

const technicalColumnDefinition: Partial<ColDef> = {
  resizable: false,
  headerValueGetter: () => '',
  suppressHeaderMenuButton: true,
  suppressColumnsToolPanel: true,
  floatingFilter: false,
  sortable: false,
  pinned: true,
  lockPinned: true,
  lockPosition: 'right',
  lockVisible: true,
};

const stringWithMasterDetail = {
  ...technicalColumnDefinition,
  valueFormatter: () => '',
  width: 65,
  minWidth: 65,
  cellRenderer: 'agGroupCellRenderer',
} as ColTypeDef<IBlotterTradeItem>;

const additionalColumns: Record<string, ColTypeDef<IBlotterTradeItem>> = {
  stringWithMasterDetail,
};

const MaxItemsCount = 10_000;

export function TradesBlotterServerSideGrid({ setLastBlotterRequest }: Props) {
  const getFilterValuesWithDispatch = useGetFilterValuesWithApi(tradesBlotterApi);
  const {
    columnDefinitions,
    blotterRequest,
    columnState,
    columnGroupState,
    pageSizeState,
    isBlotterRefreshable,
    onBlotterColumnStateChanged,
    onSetActiveItem,
    onItemsReceived,
    onItemsRequested,
    onRequestFailed,
    onPageSizeChanged,
  } = useTradesBlotterState('trades');

  const gridOptions = useTradeBlotterServerSideGridOptions<'trades', ExternalFilter>({
    columnDefinitions,
    blotterRequest,
    columnState,
    columnGroupState,
    pageSizeState,
    isBlotterRefreshable,
    onBlotterColumnStateChanged,
    onItemsReceived,
    onItemsRequested,
    onRequestFailed,
    onPageSizeChanged,
    setLastBlotterRequest,
    searchItems: searchItems,
    getFilterValues: getFilterValuesWithDispatch,
    getHeaderIntlKey,
    additionalColumns,
  });
  const isInternalUser = useAppSelector(getIsUserInternal);
  const [isTruncated, setIsTruncated] = useState(false);

  return (
    <>
      <ServerSideBlotterGrid<TradesOrProductsBlotterRequest, IBlotterTradeItem>
        name="tradesBlotter"
        logAnalyticName="Blotter My Trades"
        masterDetail
        detailCellRenderer={SensitivityDetailCellRenderer}
        detailRowAutoHeight
        e2e="trade-blotter"
        canSetActive={canSetActive}
        getRowId={getRowId}
        modules={modules}
        onSetActiveItem={onSetActiveItem}
        {...gridOptions}
        keepDetailRows
        useNativePagination={true}
        maxRowsCount={MaxItemsCount}
        setIsTruncated={setIsTruncated}
      />
      <PageLessFooter maxItemsCount={MaxItemsCount} isTruncated={isTruncated}>
        {isInternalUser && <CreditCheckBadge />}
      </PageLessFooter>
    </>
  );
}
